import React, { useState } from 'react';
import { ProvenanceMetadata } from '../types';

/**
 * Color mapping for provenance source types.
 * Consistent with Phase 5 source color coding conventions.
 */
const SOURCE_COLORS: Record<string, { badge: string; label: string }> = {
  iicrc: { badge: 'bg-blue-100 text-blue-800 border-blue-200', label: 'IICRC' },
  building_code: { badge: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Code' },
  nfip: { badge: 'bg-red-100 text-red-800 border-red-200', label: 'NFIP' },
  expert: { badge: 'bg-purple-100 text-purple-800 border-purple-200', label: 'Expert' },
  data_pattern: { badge: 'bg-green-100 text-green-800 border-green-200', label: 'Pattern' },
};

interface ProvenanceBadgeProps {
  provenance: ProvenanceMetadata;
  showSources: boolean;
}

/**
 * Inline provenance badge with expandable citation detail.
 * Shows source type with color coding; click to expand reference,
 * summary, and copy-paste-ready applicable text.
 */
export const ProvenanceBadge: React.FC<ProvenanceBadgeProps> = ({ provenance, showSources }) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  // Hidden by default; only show on first-occurrence items
  if (!showSources || !provenance.is_first_occurrence) return null;

  const colors = SOURCE_COLORS[provenance.source_type] || SOURCE_COLORS.expert;

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(provenance.applicable_text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <span className="inline-flex flex-col">
      <button
        onClick={() => setExpanded(!expanded)}
        className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded border ${colors.badge} cursor-pointer hover:opacity-80 transition-opacity`}
      >
        {colors.label}: {provenance.reference.length > 30
          ? provenance.reference.slice(0, 30) + '...'
          : provenance.reference}
      </button>

      {expanded && (
        <div className="mt-1 p-2 bg-white border rounded shadow-sm text-sm max-w-md">
          <p className="font-medium text-gray-800">{provenance.reference}</p>
          <p className="mt-1 text-gray-600">{provenance.summary}</p>
          <div className="mt-2 bg-gray-50 border rounded p-2 font-mono text-xs text-gray-700 whitespace-pre-wrap">
            {provenance.applicable_text}
          </div>
          <button
            onClick={handleCopy}
            className="mt-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
          >
            {copied ? 'Copied!' : 'Copy citation'}
          </button>
        </div>
      )}
    </span>
  );
};
